import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  IconButton,
  Box,
  FormHelperText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  postEmployeeEducations,
  getEmployeeEducations,
  putEmployeeEducations,
  deleteEmployeeEducations,
} from "app/services/Services";
import { useParams } from "react-router-dom";
import {
  generateErrorMessage,
  generateSuccessMessage,
} from "app/utils/GenerateMessage";
import ConfirmModal from "./ConfirmModal";
import {
  T,
  sectionCardSx,
  muiFieldSx,
  SectionHeader,
  StatusChip,
  PrimaryButton,
  SecondaryButton,
  LoadingBlock,
  ErrorBlock,
} from "./shared/formKit";

const educationSchema = Yup.object().shape({
  education: Yup.array()
    .of(
      Yup.object().shape({
        degree: Yup.string().required("Degree/Qualification is required"),
        institution: Yup.string().required("Institution is required"),
        year_of_passing: Yup.string()
          .matches(/^\d{4}$/, "Enter a valid year")
          .required("Year of Passing is required"),
        grade: Yup.string().required("Grade/Percentage is required"),
      })
    )
    .min(1, "At least one education entry is required"),
});

const EducationDetails = ({ onCompleted = null }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [existingEducation, setExistingEducation] = useState([]);
  const [employeeId] = useState(id);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isEdit = existingEducation.length > 0;

  useEffect(() => {
    loadExistingEducation();
  }, [id]);

  const loadExistingEducation = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const response = await getEmployeeEducations(id);
      if (response?.status === 200) {
        setExistingEducation(response.data?.data || []);
      } else {
        setExistingEducation([]);
      }
    } catch (err) {
      console.error(err);
      setExistingEducation([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const getInitialValues = () => {
    if (isEdit) {
      return {
        education: existingEducation.map((e) => ({
          id: e.id,
          degree: e.degree || "",
          institution: e.institution || "",
          year_of_passing: e.year_of_passing?.toString() || "",
          grade: e.grade || "",
        })),
      };
    }
    return { education: [{ degree: "", institution: "", year_of_passing: "", grade: "" }] };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const updated = values.education.filter((e) => e.id);
      const created = values.education.filter((e) => !e.id);

      if (updated.length) {
        await putEmployeeEducations(
          {
            education: updated.map((e) => ({
              id: e.id,
              degree: e.degree,
              institution: e.institution,
              yearOfPassing: parseInt(e.year_of_passing) || null,
              grade: e.grade,
            })),
          },
          employeeId
        );
      }

      if (created.length) {
        await postEmployeeEducations(employeeId, {
          employeeId,
          education: created.map((e) => ({
            degree: e.degree,
            institution: e.institution,
            yearOfPassing: parseInt(e.year_of_passing) || null,
            grade: e.grade,
          })),
        });
      }

      generateSuccessMessage("Education records saved successfully");
      await loadExistingEducation();
      if (onCompleted) onCompleted(values.education);
    } catch (err) {
      generateErrorMessage(err.response?.data?.message || "Failed to save record");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (educationId, removeFromForm) => {
    if (!educationId) {
      removeFromForm();
      return;
    }
    setDeleteTarget({ educationId, removeFromForm });
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { educationId } = deleteTarget;

    try {
      setLoading(true);
      await deleteEmployeeEducations([educationId]);
      generateSuccessMessage("Record deleted successfully");
      await loadExistingEducation();
      setConfirmModalOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      generateErrorMessage(err.response?.data?.message || "Failed to delete record");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={sectionCardSx}>
        <LoadingBlock label="Loading education data…" />
      </Box>
    );
  }

  return (
    <Box>
      {loadError && <ErrorBlock message="Failed to load education data." onRetry={loadExistingEducation} />}

      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={educationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form>
            <FieldArray name="education">
              {({ push, remove }) => (
                <Box sx={sectionCardSx}>
                  <SectionHeader
                    title="Educational background"
                    action={
                      <SecondaryButton
                        type="button"
                        onClick={() => push({ degree: "", institution: "", year_of_passing: "", grade: "" })}
                        disabled={isSubmitting}
                      >
                        <AddIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: "6px" }} />
                        Add education
                      </SecondaryButton>
                    }
                  />

                  {values.education.map((edu, index) => (
                    <Box
                      key={`${edu.id || "new"}-${index}`}
                      sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: T.inputR, padding: "16px 18px", mb: "16px" }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Box sx={{ fontFamily: T.body_f, fontSize: "13.5px", fontWeight: 600, color: T.ink }}>
                            Education {index + 1}
                          </Box>
                          <StatusChip label={edu.id ? "Existing" : "New"} tone={edu.id ? "neutral" : "warning"} />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(edu.id, () => remove(index))}
                          disabled={isSubmitting || loading}
                          sx={{ color: T.danger }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            name={`education.${index}.degree`} label="Degree"
                            value={edu.degree} onChange={handleChange} onBlur={handleBlur}
                            error={touched.education?.[index]?.degree && Boolean(errors.education?.[index]?.degree)}
                            helperText={touched.education?.[index]?.degree && errors.education?.[index]?.degree}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            name={`education.${index}.institution`} label="Institution"
                            value={edu.institution} onChange={handleChange} onBlur={handleBlur}
                            error={touched.education?.[index]?.institution && Boolean(errors.education?.[index]?.institution)}
                            helperText={touched.education?.[index]?.institution && errors.education?.[index]?.institution}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            name={`education.${index}.year_of_passing`} label="Year of passing"
                            value={edu.year_of_passing} onChange={handleChange} onBlur={handleBlur}
                            inputProps={{ maxLength: 4, pattern: "[0-9]*" }}
                            error={touched.education?.[index]?.year_of_passing && Boolean(errors.education?.[index]?.year_of_passing)}
                            helperText={touched.education?.[index]?.year_of_passing && errors.education?.[index]?.year_of_passing}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            name={`education.${index}.grade`} label="Grade/Percentage"
                            value={edu.grade} onChange={handleChange} onBlur={handleBlur}
                            error={touched.education?.[index]?.grade && Boolean(errors.education?.[index]?.grade)}
                            helperText={touched.education?.[index]?.grade && errors.education?.[index]?.grade}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  {typeof errors.education === "string" && (
                    <FormHelperText error>{errors.education}</FormHelperText>
                  )}

                  <Box mt={2} display="flex" gap={2}>
                    <PrimaryButton disabled={isSubmitting || !values.education.length}>
                      {isSubmitting ? "Saving…" : "Save / update"}
                    </PrimaryButton>
                    <SecondaryButton type="button" onClick={loadExistingEducation} disabled={isSubmitting}>
                      Refresh
                    </SecondaryButton>
                  </Box>
                </Box>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
      <ConfirmModal
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleConfirmDelete}
        title="Delete Education Record?"
        description="Are you sure you want to delete this record? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />
    </Box>
  );
};

export default EducationDetails;
